import { argbIntToHex, isTrailCameraPin, TRAIL_CAMERA_PIN_ID_PREFIX } from '@wehunt/ui'
import type { GeoArea, GeoPt, MapObject } from '@wehunt/ui'
import type { HuntingGroundAreaDto } from './api/huntingGroundsApi'
import type { TeamCameraPinDto } from './api/teamCameraPinsApi'
import type { TeamMapPinDto } from './api/teamMapPinsApi'

// Pure DTO → domain mappers, kept free of React/auth imports so non-hook callers
// (e.g. loadCopyableTeamMap) can reuse them without pulling in the whole hook
// dependency chain.

const toGeoPt = (c: { latitude: number; longitude: number }): GeoPt => ({
  lat: c.latitude,
  lon: c.longitude,
})

/**
 * Drops a ring's closing point, so every ring this app holds is OPEN — the first
 * point is not repeated at the end.
 *
 * That is the shape the rest of the web assumes: `polygonUnion` closes rings on the
 * way into the clipper and strips the closing point on the way out, `printGeometry`
 * does the same, and the map editor lifts `rings[0]` straight into terra-draw, which
 * rejects an already-closed ring — it would double the seam and put every vertex
 * index reported back one off from the list the editor is holding.
 *
 * The wire does not honour that. A parcel picked off the cadastral overlay is stored
 * with the outline exactly as the property service states it, and a GIS polygon ring
 * is closed by specification, so a property-sourced ground arrives here closed. Every
 * ring the app builds from the API comes through this mapper, so normalizing once
 * here is what makes the invariant true everywhere rather than leaving each consumer
 * to defend itself — which is how opening such a ground's parcel for point editing
 * came to throw and blank the whole map page.
 *
 * The whole trailing run of first-point copies goes, not just the last one: stored
 * rings — forbidden areas above all — end in their first point up to a dozen times,
 * from imports and writers that close a ring that was already closed. Each repeat is
 * a zero-length edge, so dropping the run changes no outline, while dropping only
 * one left such a ring closed, and the editor offering no way to reshape it.
 *
 * Nothing before that run is touched. A ring that passes back through its starting
 * coordinate part-way round is a real outline, and the first point itself always
 * stays — a ring that is nothing but one point is degenerate, not empty, and an
 * empty ring would read downstream as a deleted area.
 */
function openRing(ring: GeoPt[]): GeoPt[] {
  const first = ring[0]
  let end = ring.length
  while (end > 1 && ring[end - 1].lat === first.lat && ring[end - 1].lon === first.lon) end--
  return end === ring.length ? ring : ring.slice(0, end)
}

/**
 * Reads the ring list when the API sends one, and falls back to the flat coordinate
 * list an older deploy sends.
 *
 * The fallback is not defensive coding for its own sake: the API and this app ship on
 * two independently tagged trains with no ordering between them, so for the length of
 * the window between deploys a browser running the new bundle can be talking to the
 * old API. The arm is deleted in the release after the ring list is everywhere.
 *
 * Exported separately from `dtoToGeoArea` so the save diff can read the wire through
 * exactly this lens: it compares the editor's rings against the stored ones, and the
 * two are only comparable if both have been through the same normalization.
 */
export function dtoToGeoRings(dto: HuntingGroundAreaDto): GeoPt[][] {
  return dto.rings?.length
    ? dto.rings.map((ring) => openRing(ring.map(toGeoPt)))
    : [openRing((dto.coordinates ?? []).map(toGeoPt))]
}

export function dtoToGeoArea(dto: HuntingGroundAreaDto): GeoArea {
  const rings = dtoToGeoRings(dto)
  return {
    id: dto.id,
    name: dto.name ?? '',
    rings,
    // An API that does not send the count has not sent holes either, so every ring
    // it sent is an outer. Defaulting the other way would cut a ring out of the
    // shape it belongs to.
    outerCount: dto.outerRingCount ?? rings.length,
    color: dto.color ?? undefined,
    areaSource: dto.areaSource ?? undefined,
    areaGroup: dto.areaGroup ?? undefined,
    coordinateSource: dto.coordinateSource ?? undefined,
    // Only a well-formed [SW, NE] pair is carried through; anything else is treated
    // as absent so the map seed falls back to its regional default rather than
    // framing on a malformed box.
    bounds: dto.bounds?.length === 2
      ? [toGeoPt(dto.bounds[0]), toGeoPt(dto.bounds[1])]
      : undefined,
  }
}

/**
 * The stored pin type as the map should draw it. Web used to write its own ids
 * (`animal_moose`, `atel`, `salt_rock`, …) and this function aliased them to
 * the canonical ids on read; those documents were migrated in place on
 * 2026-09-17 (`tools/map-pin-type-migration`), so nothing is aliased any more.
 * The vocabulary is the Remote Config catalog, runtime data this mapper cannot
 * know, so any id is passed through as is and an unknown one renders as
 * `other`. Only a missing type becomes `pin`.
 */
export function canonicalPinType(raw: string | null): string {
  return raw ?? 'pin'
}

export function dtoToMapObject(dto: TeamMapPinDto): MapObject {
  const type = canonicalPinType(dto.type)
  const obj: MapObject = {
    id: dto.id,
    type,
    lat: dto.latitude,
    lon: dto.longitude,
    label: dto.name ?? '',
    // Only forward these when the pin actually has them. `argbIntToHex`
    // substitutes the line default for a missing colour, which would override a
    // typed pin's own artwork, and an empty symbol is not a glyph to draw.
    ...(dto.customSymbol ? { customSymbol: dto.customSymbol } : {}),
    ...(dto.color == null ? {} : { color: argbIntToHex(dto.color) }),
    // The folder the app filed this pin in, when it is in one. Absent rather
    // than null at the root, so an object built here compares equal to one the
    // editor built for an unfiled pin — the editor's save diffs objects, and a
    // stray `folderId: null` would read as a change nobody made.
    ...(dto.folderId ? { folderId: dto.folderId } : {}),
    ...(dto.orderIndex == null ? {} : { orderIndex: dto.orderIndex }),
    ...(dto.description ? { description: dto.description } : {}),
  }
  // Carry the stored picture ids as EXISTING images. The download URL is left
  // empty here because resolving it is async (getDownloadURL) — this mapper is
  // pure/sync; the load hook (useGroupMapPins) fills each `url` in. `photos` is
  // only set when the pin actually has pictures, so an imageless pin stays a
  // bare object (keeps existing dtoToMapObject callers/assertions unchanged).
  if (dto.images.length > 0) {
    obj.photos = dto.images.map((id) => ({ id, url: '' }))
  }
  return obj
}

/**
 * Namespace for a trail camera's pin id. Camera pins are merged into the same
 * list as the `mapPins` documents but come from a different collection in a
 * different database, so nothing guarantees the two id spaces don't overlap.
 *
 * Declared in `@wehunt/ui` rather than here, and re-exported under this name
 * for the write paths that already read it: the pin panel and the team's pin
 * count have to tell a camera from a map pin too, and two copies of the prefix
 * would be two places for it to drift from the one this file mints ids with.
 */
export { TRAIL_CAMERA_PIN_ID_PREFIX as CAMERA_PIN_ID_PREFIX, isTrailCameraPin }

/**
 * Whether a map object is a pin from the viewer's OWN collection rather than the
 * team's. Marked by the hook that loads it (the route it came from is what knows
 * this), not by the mapper, which sees the same DTO either way.
 *
 * Every team write path has to exclude these. Their `owner` is the hunter's uid,
 * so the team endpoints refuse them — an editor save that carried one would get
 * a 403 for a pin the user could see and had every reason to think was theirs to
 * move, and one that had been REMOVED from the list would be read as a deletion
 * of a team pin that never existed.
 */
export function isPrivatePin(object: Pick<MapObject, 'isPrivate'>): boolean {
  return object.isPrivate === true
}

/**
 * A trail camera as a map pin. Mobile draws one pin per located camera on the
 * same map as the ordinary map pins, so the team map here does the same — the
 * camera count in the Overview and the Map tab reads the pins it produces.
 *
 * The id is namespaced because these pins are merged into the same list as the
 * `mapPins` documents but come from a different collection in a different
 * database, so nothing guarantees the two id spaces don't overlap. The prefix
 * also marks them as not-a-map-pin for anything reading the list: a camera's
 * position lives in Burrel and cannot be saved through the map editor, which is
 * why the editor is fed the bare `mapPins` and never these.
 */
export function dtoToCameraMapObject(dto: TeamCameraPinDto): MapObject {
  return {
    id: `${TRAIL_CAMERA_PIN_ID_PREFIX}${dto.id}`,
    // A camera the viewer owns draws as their own; one they only reach through a
    // share draws as a shared camera, which is the distinction the pin registry
    // and the team's camera count already make.
    type: dto.ownedByMe ? 'camera' : 'camera_shared',
    lat: dto.latitude,
    lon: dto.longitude,
    label: dto.name ?? '',
  }
}
